import React from 'react';
import styled from 'styled-components';
import startCase from 'lodash/startCase';
import truncate from 'lodash/truncate';
import startsWith from 'lodash/startsWith';
import replace from 'lodash/replace';
import moment from 'moment';
import { Link } from 'react-router-dom';

import Currency from 'components/common/Currency';
import TransactionStatus from 'components/common/TransactionStatus';

import EmptyImg from 'assets/images/placeholder_empty_2.png';

import SvgIcon from '@twicapp/ui/components/SvgIcon';
import Subtitle2 from '@twicapp/ui/components/Subtitle2';
import Body2 from '@twicapp/ui/components/Body2';
import Button from '@twicapp/ui/components/legacy/Button';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

import { ReactComponent as CaretRightSvg } from '@twicapp/ui/icons/caret-right.svg';
import { ReactComponent as TransactionsSvg } from '@twicapp/ui/icons/transactions.svg';
import { ReactComponent as SubscriptionsSvg } from '@twicapp/ui/icons/subscriptions.svg';

import {
  SVG_ICON_COLORS,
  SVG_ICON_SIZES,
  BUTTON_VARIANTS,
} from '@twicapp/ui/constants/properties';

import { TransactionRoutes, SubscriptionRoutes } from 'utils/routeGenerator';

const Container = styled.div``;

const ItemContainer = styled.div`
  padding-bottom: 16px;
  padding-top: 16px;
  border-bottom: 1px solid
    ${({
      theme: {
        colors: {
          border: { primary },
        },
      },
    }) => primary};
  cursor: pointer;
`;

const ItemLink = styled(Link)`
  display: flex;
  justify-content: space-between;
`;
const MiddleDot = styled(Subtitle2)`
  display: inline-block;
  padding: 0 3px;
`;

const LeftContent = styled.div`
  display: flex;
  flex: 1;
`;

const RightContent = styled.div`
  display: flex;
`;

const StatusContainer = styled.div`
  margin-right: 30px;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    margin-right: 0;
  }
`;

const IconContainer = styled.div`
  display: flex;
`;

const OrderIcon = styled(SvgIcon).attrs({
  icon: TransactionsSvg,
  stroke: SVG_ICON_COLORS.SECONDARY,
  size: SVG_ICON_SIZES.MEDIUM,
})``;

const SubscriptionIcon = styled(SvgIcon).attrs({
  icon: SubscriptionsSvg,
  stroke: SVG_ICON_COLORS.SECONDARY,
  size: SVG_ICON_SIZES.MEDIUM,
})``;

const ArrowIcon = styled(SvgIcon).attrs({
  icon: CaretRightSvg,
  fill: SVG_ICON_COLORS.SECONDARY,
})``;

const ImageContainer = styled.div`
  margin-right: 24px;
  background-color: ${({
    theme: {
      colors: {
        background: { tertiary },
      },
    },
  }) => tertiary};
  border-radius: 7px;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    flex-direction: row;
    justify-content: space-between;
    flex: 1;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled(Subtitle2)``;

const SubInfo = styled(Body2)`
  display: block;
  max-width: 352px;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
`;

const EmptyIcon = styled.img.attrs({
  src: EmptyImg,
  alt: 'Empty list',
})`
  width: 176px;
  height: 141px;
  object-fit: contain;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NoItemsDescription = styled(Body2)`
  display: block;
  max-width: 355px;
  text-align: center;
  margin-top: 17px;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const LoadMoreButton = styled(Button).attrs({
  variant: BUTTON_VARIANTS.GHOST,
})``;

const generateTitle = (list_item: $TSFixMe) => {
  const { transaction_subtype } = list_item;
  const { product_title, vendor_title } =
    (list_item.transaction && list_item.transaction.store) || {};
  const { title } = list_item;
  switch (transaction_subtype) {
    case 'order_purchase':
      if (startsWith(product_title, vendor_title)) {
        return startCase(product_title);
      }
      return `${startCase(vendor_title)} ${startCase(product_title)}`;

    case 'order_purchase_stripe':
      return `${startCase(
        list_item.transaction.card.authorization_details.merchant_data.name
      )}`;
    case 'subscription_renew':
      return startCase(`${vendor_title} subscription`);
    case 'reimbursement_approved':
      return list_item.transaction
        ? startCase(
            `${replace(
              list_item.transaction.reimbursement.category,
              '_',
              ' '
            )} reimbursement`
          )
        : '';
    case 'reimbursement_adjustment':
      return startCase(
        `${replace(
          list_item.transaction.reimbursement.category,
          '_',
          ' '
        )} reimbursement adjustment`
      );
    default:
      return title;
  }
};

const HistoryList = ({
  items,
  has_more_items,
  onNextPage,
  employee,
  type,
  description,
}: $TSFixMe) => (
  <Container>
    {items.length > 0 ? (
      items.map((item: $TSFixMe) => {
        const { id: transaction_id } = item.transaction || {};
        const { currency } = item.currency_info || {};
        const txn = transaction_id ? item.transaction.id : item.id;

        return (
          <ItemContainer key={txn}>
            <ItemLink
              to={
                type === 'order'
                  ? TransactionRoutes.detail({ transaction_id: txn })
                  : SubscriptionRoutes.detail({ subscription_id: txn })
              }
            >
              <LeftContent>
                <ImageContainer>
                  {type === 'order' ? <OrderIcon /> : <SubscriptionIcon />}
                </ImageContainer>
                <DetailContainer>
                  <InfoContainer>
                    <Title>
                      {truncate(generateTitle(item), { length: 40 })}
                    </Title>
                    <SubInfo>
                      <Currency
                        amount={
                          item.payment ? item.payment.total : item.total_amount
                        }
                        transaction_currency={
                          currency ? currency : employee.settings.currency
                        }
                      />{' '}
                      <MiddleDot>&middot;</MiddleDot>
                      {` Purchased ${moment(item.created).format('ll')}`}
                    </SubInfo>
                  </InfoContainer>
                  <StatusContainer>
                    <TransactionStatus
                      status={item.status}
                      show_icon={true}
                      size={'small'}
                    />
                  </StatusContainer>
                </DetailContainer>
              </LeftContent>
              <RightContent>
                <IconContainer>
                  <ArrowIcon />
                </IconContainer>
              </RightContent>
            </ItemLink>
          </ItemContainer>
        );
      })
    ) : (
      <EmptyContainer>
        <EmptyIcon />
        <NoItemsDescription>{description}</NoItemsDescription>
      </EmptyContainer>
    )}
    {has_more_items && (
      <ButtonRow>
        <LoadMoreButton onClick={onNextPage}>Load More</LoadMoreButton>
      </ButtonRow>
    )}
  </Container>
);

export default HistoryList;
